import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiCreditCard, FiSmartphone, FiDownload, FiCheckCircle, FiAlertCircle,
    FiCalendar, FiHash, FiShoppingBag, FiClock, FiShield,
} from 'react-icons/fi';
import {
    generatePdfCarteirinhaApi,
    getProfileApi,
    getUserCarteirinhaByCpfFisicaPngApi,
    getUserCarteirinhaByCpfPngApi,
    listCarteirinhasByUserIdApi,
    validarUserByCpfApi,
} from '../../Api';
import ProfileManager from '../../components/ProfileManager';
import { notify } from '../../components/Notify';
import { isAuthenticatedUEN } from '../../utils';

// ---------- Helpers ----------

// A validade é uma DATA (dia 31/03): a carteirinha vale ATÉ O FIM desse dia e só
// expira no dia seguinte (01/04). Tratamos por DIA, recortando ano/mês/dia sem
// depender de hora/fuso, e comparamos contra o fim do dia local.
function fimDaValidade(value) {
    if (!value) return null;
    const s = String(value);
    const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    let y, m, d;
    if (br) { d = +br[1]; m = +br[2]; y = +br[3]; }
    else if (iso) { y = +iso[1]; m = +iso[2]; d = +iso[3]; }
    else { const dt = new Date(s); if (Number.isNaN(dt.getTime())) return null; y = dt.getFullYear(); m = dt.getMonth() + 1; d = dt.getDate(); }
    return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
}

function formatDate(value) {
    if (!value) return '—';
    const s = String(value);
    const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (br) return `${br[1]}/${br[2]}/${br[3]}`;
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
    return '—';
}

function daysUntil(value) {
    const fim = fimDaValidade(value);
    if (fim == null) return null;
    return Math.ceil((fim - Date.now()) / (1000 * 60 * 60 * 24));
}

function statusBadge(carteirinha) {
    const status = String(carteirinha.status_aprovacao || '').toLowerCase();
    const fim = fimDaValidade(carteirinha.validade_carteirinha);
    const expirada = fim != null && fim < Date.now();

    if (status === 'reprovado') return { label: 'Reprovada', tone: 'danger', Icon: FiAlertCircle };
    if (expirada) return { label: 'Expirada', tone: 'muted', Icon: FiClock };
    if (status === 'aprovado') return { label: 'Ativa', tone: 'success', Icon: FiCheckCircle };
    if (status === 'pendente') return { label: 'Em análise', tone: 'warning', Icon: FiClock };
    return { label: status || 'Indefinido', tone: 'muted', Icon: FiAlertCircle };
}

const toneStyles = {
    success: { bg: '#ECFDF5', color: '#047857', border: '#A7F3D0' },
    warning: { bg: '#FFFBEB', color: '#B45309', border: '#FCD34D' },
    danger:  { bg: '#FFF1F2', color: '#9F1239', border: '#FECDD3' },
    muted:   { bg: '#F1F5F9', color: '#475569', border: '#CBD5E1' },
};

function isApprovedAndActive(c) {
    const aprovado = String(c.status_aprovacao || '').toLowerCase() === 'aprovado';
    const fim = fimDaValidade(c.validade_carteirinha);
    const naoExpirada = fim == null || fim >= Date.now();
    return aprovado && naoExpirada;
}

// Ordena por ano_referencia desc e depois id desc.
function sortMostRecent(a, b) {
    const ya = Number(a.ano_referencia) || 0;
    const yb = Number(b.ano_referencia) || 0;
    if (yb !== ya) return yb - ya;
    return (b.id || 0) - (a.id || 0);
}

// ---------- Página ----------

export default function MinhaCarteirinha() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [carteirinhas, setCarteirinhas] = useState([]);
    const [imagemUrl, setImagemUrl] = useState(null);
    const [imagemUrlFisica, setImagemUrlFisica] = useState(null);
    const [erroFisica, setErroFisica] = useState(false);
    const [erroDigital, setErroDigital] = useState(false);
    const [tipoVisualizacao, setTipoVisualizacao] = useState('digital');

    const blobUrlsRef = useRef([]);
    // Espelho do estado pra o polling comparar sem virar dependência do efeito.
    const carteirinhasRef = useRef(carteirinhas);
    useEffect(() => { carteirinhasRef.current = carteirinhas; }, [carteirinhas]);

    // Busca as imagens (digital PNG + física PNG) da carteirinha aprovada.
    // Reusada na carga inicial e quando o polling detecta a aprovação.
    const carregarImagens = useCallback(async (cpf, signal) => {
        if (!cpf) return;
        const [digitalRes, fisicaRes] = await Promise.allSettled([
            getUserCarteirinhaByCpfPngApi({ cpf, signal }),
            getUserCarteirinhaByCpfFisicaPngApi({ cpf, signal }),
        ]);
        if (digitalRes.status === 'fulfilled') {
            const url = URL.createObjectURL(digitalRes.value.data);
            blobUrlsRef.current.push(url);
            setImagemUrl(url);
            setErroDigital(false);
        } else { setErroDigital(true); }
        if (fisicaRes.status === 'fulfilled') {
            const url = URL.createObjectURL(fisicaRes.value.data);
            blobUrlsRef.current.push(url);
            setImagemUrlFisica(url);
            setErroFisica(false);
        } else { setErroFisica(true); }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        let isMounted = true;

        const carregar = async () => {
            if (!isAuthenticatedUEN()) { navigate('/login'); return; }

            try {
                const [resProfile, resCarteirinhas] = await Promise.all([
                    getProfileApi({ signal: controller.signal }),
                    listCarteirinhasByUserIdApi({ signal: controller.signal }),
                ]);
                if (!isMounted) return;

                setUserData(resProfile.data);
                const lista = resCarteirinhas.data?.data || [];
                setCarteirinhas(lista);

                // Imagens só são buscadas se houver carteirinha APROVADA e válida.
                // Pendente/reprovada/expirada -> placeholder (sem chamar a API).
                if (!lista.some(isApprovedAndActive)) return;

                await carregarImagens(resProfile.data.cpf, controller.signal);
            } catch (err) {
                if (controller.signal.aborted) return;
                navigate('/login');
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        carregar();
        return () => {
            isMounted = false;
            controller.abort();
            blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
            blobUrlsRef.current = [];
        };
    }, [navigate, carregarImagens]);

    // Se a digital não carregou mas a física sim, troca a aba ativa.
    useEffect(() => {
        if (tipoVisualizacao === 'digital' && !imagemUrl && imagemUrlFisica) {
            setTipoVisualizacao('fisica');
        }
    }, [imagemUrl, imagemUrlFisica, tipoVisualizacao]);

    // Há alguma carteirinha aguardando aprovação? (controla o polling)
    const temPendente = useMemo(
        () => carteirinhas.some(c => String(c.status_aprovacao || '').toLowerCase() === 'pendente'),
        [carteirinhas]
    );

    // POLLING — enquanto houver carteirinha PENDENTE e o aluno estiver nesta
    // página, consulta a cada 1 min se foi aprovada. Quando aprovar: atualiza a
    // lista (o badge vira "Ativa") e carrega as imagens pra mostrar na hora.
    // Para sozinho quando não há mais pendente (temPendente vira false) ou ao
    // sair da página (cleanup).
    useEffect(() => {
        if (!temPendente) return;
        const cpf = userData?.cpf;
        const controller = new AbortController();
        let cancelado = false;

        const verificar = async () => {
            try {
                const res = await listCarteirinhasByUserIdApi({ signal: controller.signal });
                if (cancelado) return;
                const lista = res.data?.data || [];
                const tinhaAprovada = carteirinhasRef.current.some(isApprovedAndActive);
                const temAprovadaAgora = lista.some(isApprovedAndActive);
                setCarteirinhas(lista);
                // Transição pendente -> aprovada: avisa e busca as imagens.
                // (sem signal: não queremos que o cleanup do efeito aborte o
                //  fetch das imagens quando o polling parar por já ter aprovado.)
                if (!tinhaAprovada && temAprovadaAgora && cpf) {
                    notify('Sua carteirinha foi aprovada! 🎉', 'success');
                    carregarImagens(cpf);
                }
            } catch {
                // silencioso — tenta de novo no próximo ciclo.
            }
        };

        const id = setInterval(verificar, 60000);
        return () => {
            cancelado = true;
            controller.abort();
            clearInterval(id);
        };
    }, [temPendente, userData, carregarImagens]);

    // Carteirinha "ativa": prioriza aprovada+válida; senão a mais recente.
    const activeCard = useMemo(() => {
        if (carteirinhas.length === 0) return null;
        const approved = [...carteirinhas].filter(isApprovedAndActive).sort(sortMostRecent);
        if (approved.length > 0) return approved[0];
        return [...carteirinhas].sort(sortMostRecent)[0];
    }, [carteirinhas]);

    const otherCards = useMemo(
        () => activeCard ? carteirinhas.filter(c => c.id !== activeCard.id).sort(sortMostRecent) : [],
        [carteirinhas, activeCard]
    );

    const hasImage = !!(imagemUrl || imagemUrlFisica);

    if (loading) {
        return (
            <div style={styles.loading}>
                <div style={styles.spinner} />
                <p>Carregando suas carteirinhas...</p>
                <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <ProfileManager />

            {/* ---------- Cabeçalho ---------- */}
            <header style={styles.header}>
                <h1 style={styles.title}>Minhas carteirinhas</h1>
                <p style={styles.subtitle}>
                    Sua carteirinha de estudante UEN — versão digital sempre à mão.
                </p>
            </header>

            {/* ---------- Sem nenhuma carteirinha ---------- */}
            {!activeCard ? (
                <div style={styles.emptyCard}>
                    <FiCreditCard size={48} color="#94A3B8" style={{ marginBottom: 12 }} />
                    <h2 style={styles.emptyTitle}>Nenhuma carteirinha emitida</h2>
                    <p style={styles.emptyMessage}>
                        Você ainda não tem nenhuma carteirinha no sistema.
                        Emita a sua para começar a usar os benefícios.
                    </p>
                    <button style={styles.primaryBtn} onClick={() => navigate('/produtos')}>
                        <FiShoppingBag size={16} /> Emitir carteirinha
                    </button>
                </div>
            ) : (
                <>
                    {/* ---------- HERO SPLIT ---------- */}
                    <section style={styles.hero} data-hero>
                        {/* Coluna esquerda: imagem */}
                        <div style={styles.imageColumn} data-image-col>
                            {hasImage ? (
                                <>
                                    {/* Toggle Digital/Física — só se ambas existirem */}
                                    {(imagemUrl && imagemUrlFisica) && (
                                        <div style={styles.toggleContainer}>
                                            <button
                                                style={styles.toggleBtn(tipoVisualizacao === 'digital')}
                                                onClick={() => setTipoVisualizacao('digital')}
                                            >
                                                <FiSmartphone size={15} /> Digital
                                            </button>
                                            <button
                                                style={styles.toggleBtn(tipoVisualizacao === 'fisica')}
                                                onClick={() => setTipoVisualizacao('fisica')}
                                            >
                                                <FiCreditCard size={15} /> Física
                                            </button>
                                        </div>
                                    )}

                                    {/* Imagem */}
                                    <div style={styles.imageWrapper}>
                                        {tipoVisualizacao === 'digital' && imagemUrl && (
                                            <img
                                                src={imagemUrl}
                                                alt="Carteirinha digital"
                                                style={styles.imageDigital}
                                            />
                                        )}
                                        {tipoVisualizacao === 'fisica' && imagemUrlFisica && (
                                            <img
                                                src={imagemUrlFisica}
                                                alt="Carteirinha física (PVC)"
                                                style={styles.imageFisica}
                                            />
                                        )}
                                        {erroDigital && tipoVisualizacao === 'digital' && (
                                            <div style={styles.imageError}>
                                                <FiAlertCircle size={20} />
                                                Não foi possível carregar a digital.
                                            </div>
                                        )}
                                        {erroFisica && tipoVisualizacao === 'fisica' && (
                                            <div style={styles.imageError}>
                                                <FiAlertCircle size={20} />
                                                Não foi possível carregar a física.
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <ImagePlaceholder card={activeCard} />
                            )}
                        </div>

                        {/* Coluna direita: info + ações */}
                        <div style={styles.infoColumn} data-info-col>
                            <InfoPanel
                                card={activeCard}
                                imagemUrl={imagemUrl}
                                imagemUrlFisica={imagemUrlFisica}
                                userNome={userData?.nome}
                                userCpf={userData?.cpf}
                                onComprar={() => navigate('/produtos')}
                            />
                        </div>
                    </section>

                    {/* ---------- Outras carteirinhas ---------- */}
                    {otherCards.length > 0 && (
                        <section style={styles.historySection}>
                            <h2 style={styles.historyTitle}>Outras carteirinhas</h2>
                            <div style={styles.historyGrid}>
                                {otherCards.map((c) => (
                                    <HistoryCard key={c.id} carteirinha={c} />
                                ))}
                            </div>
                        </section>
                    )}
                </>
            )}

            <style>{`
                @keyframes spin{to{transform:rotate(360deg)}}
                @media (max-width: 880px) {
                    [data-hero] { flex-direction: column !important; }
                    [data-image-col], [data-info-col] {
                        max-width: 100% !important;
                        width: 100% !important;
                    }
                }
            `}</style>
        </div>
    );
}

// ---------- Componentes internos ----------

function InfoPanel({ card, imagemUrl, imagemUrlFisica, userNome, userCpf, onComprar }) {
    const badge = statusBadge(card);
    const tone = toneStyles[badge.tone];
    const dias = daysUntil(card.validade_carteirinha);
    const StatusIcon = badge.Icon;
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [gerandoAssinado, setGerandoAssinado] = useState(false);

    const tipo = useMemo(() => {
        const partes = [];
        if (card.digital !== 0) partes.push('Digital');
        if (card.solicitou_fisica === 1) partes.push('Física');
        return partes.length > 0 ? partes.join(' + ') : 'Digital';
    }, [card]);

    // Baixa o PDF oficial gerado pelo backend (canvas + jsPDF no servidor).
    // Não confundir com a versão PNG que é só pra preview.
    const handleBaixarDigital = async () => {
        if (downloadingPdf || !userCpf) return;
        setDownloadingPdf(true);
        try {
            const res = await generatePdfCarteirinhaApi({ cpf: userCpf });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `carteirinha_${(userNome || 'uen').replace(/\s+/g, '_')}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            notify('PDF gerado com sucesso!', 'success');
        } catch (err) {
            console.error('Erro ao gerar PDF da carteirinha:', err);
            notify(err.message || 'Não foi possível gerar o PDF.', 'error');
        } finally {
            setDownloadingPdf(false);
        }
    };

    // Emite o PDF com ASSINATURA DIGITAL (ICP) gerado pelo ValidarController
    // (/api/validarpdf → assinarPdf). Documento oficial validável.
    const handleEmitirAssinado = async () => {
        if (gerandoAssinado || !userCpf) return;
        setGerandoAssinado(true);
        try {
            const res = await validarUserByCpfApi({ cpf: userCpf });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `carteirinha_assinada_${(userNome || 'uen').replace(/\s+/g, '_')}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            notify('Documento assinado gerado com sucesso!', 'success');
        } catch (err) {
            console.error('Erro ao gerar PDF assinado:', err);
            notify(err.message || 'Não foi possível gerar o documento assinado.', 'error');
        } finally {
            setGerandoAssinado(false);
        }
    };

    return (
        <>
            {/* Badge de status */}
            <span style={{
                ...styles.statusBadge,
                backgroundColor: tone.bg,
                color: tone.color,
                borderColor: tone.border,
            }}>
                <StatusIcon size={14} /> {badge.label}
            </span>

            <h2 style={styles.activeTitle}>
                Carteirinha {card.ano_referencia || ''}
            </h2>
            <p style={styles.activeSubtitle}>
                {badge.tone === 'success' && 'Pronta pra usar nos seus benefícios de meia-entrada.'}
                {badge.tone === 'warning' && 'Estamos analisando sua solicitação. Em breve será liberada.'}
                {badge.tone === 'danger' && 'A solicitação foi reprovada. Entre em contato pelo suporte.'}
                {badge.tone === 'muted' && (dias != null && dias < 0
                    ? 'Renove sua carteirinha para continuar com os benefícios.'
                    : 'Confira os detalhes abaixo.')}
            </p>

            {/* Linhas de info */}
            <ul style={styles.infoList}>
                <li style={styles.infoRow}>
                    <span style={styles.infoLabel}>
                        <FiCalendar size={14} color="#94A3B8" /> Validade
                    </span>
                    <span style={styles.infoValue}>
                        {formatDate(card.validade_carteirinha)}
                        {dias != null && dias > 0 && (
                            <span style={styles.infoSub}>
                                {' '}• em {dias} dia{dias === 1 ? '' : 's'}
                            </span>
                        )}
                        {dias != null && dias <= 0 && (
                            <span style={{ ...styles.infoSub, color: '#E11D48' }}>
                                {' '}• expirada
                            </span>
                        )}
                    </span>
                </li>
                {card.numero_carteirinha && (
                    <li style={styles.infoRow}>
                        <span style={styles.infoLabel}>
                            <FiHash size={14} color="#94A3B8" /> Número
                        </span>
                        <span style={{ ...styles.infoValue, fontFamily: "'JetBrains Mono', monospace" }}>
                            {card.numero_carteirinha}
                        </span>
                    </li>
                )}
                <li style={styles.infoRow}>
                    <span style={styles.infoLabel}>
                        <FiCreditCard size={14} color="#94A3B8" /> Tipo
                    </span>
                    <span style={styles.infoValue}>{tipo}</span>
                </li>
            </ul>

            {/* Ações */}
            <div style={styles.actions}>
                {imagemUrl && (
                    <button
                        onClick={handleBaixarDigital}
                        disabled={downloadingPdf || !userCpf}
                        style={{
                            ...styles.actionPrimary,
                            opacity: (downloadingPdf || !userCpf) ? 0.7 : 1,
                            cursor: (downloadingPdf || !userCpf) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {downloadingPdf ? <span style={styles.btnSpinner} /> : <FiDownload size={16} />}
                        {downloadingPdf ? 'Gerando PDF...' : 'Baixar digital'}
                    </button>
                )}
                {imagemUrl && (
                    <button
                        onClick={handleEmitirAssinado}
                        disabled={gerandoAssinado || !userCpf}
                        style={{
                            ...styles.actionSecondary,
                            opacity: (gerandoAssinado || !userCpf) ? 0.7 : 1,
                            cursor: (gerandoAssinado || !userCpf) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {gerandoAssinado ? <span style={styles.btnSpinnerDark} /> : <FiShield size={16} />}
                        {gerandoAssinado ? 'Gerando documento...' : 'Emitir documento assinado'}
                    </button>
                )}
                {!imagemUrl && !imagemUrlFisica && badge.tone === 'muted' && (
                    <button style={styles.actionPrimary} onClick={onComprar}>
                        <FiShoppingBag size={16} /> Emitir nova carteirinha
                    </button>
                )}
            </div>
        </>
    );
}

function ImagePlaceholder({ card }) {
    const badge = statusBadge(card);
    const tone = toneStyles[badge.tone];

    const messages = {
        warning: { title: 'Em análise', text: 'Sua carteirinha será exibida aqui assim que for aprovada.' },
        danger:  { title: 'Reprovada', text: 'A solicitação foi reprovada. Entre em contato pelo suporte.' },
        muted:   { title: 'Sem imagem', text: 'A imagem da carteirinha aparece aqui após aprovação.' },
        success: { title: 'Carregando...', text: 'Buscando a imagem da sua carteirinha.' },
    };
    const msg = messages[badge.tone] || messages.muted;

    return (
        <div style={{
            ...styles.imagePlaceholder,
            background: tone.bg,
            borderColor: tone.border,
        }}>
            <FiCreditCard size={48} color={tone.color} style={{ opacity: 0.5 }} />
            <h3 style={{ ...styles.placeholderTitle, color: tone.color }}>{msg.title}</h3>
            <p style={styles.placeholderText}>{msg.text}</p>
        </div>
    );
}

function HistoryCard({ carteirinha }) {
    const badge = statusBadge(carteirinha);
    const tone = toneStyles[badge.tone];
    const StatusIcon = badge.Icon;

    return (
        <article style={styles.historyCard}>
            <div style={styles.historyTop}>
                <span style={{
                    ...styles.statusChip,
                    backgroundColor: tone.bg,
                    color: tone.color,
                    borderColor: tone.border,
                }}>
                    <StatusIcon size={12} /> {badge.label}
                </span>
                <span style={styles.historyYear}>{carteirinha.ano_referencia || '—'}</span>
            </div>
            <ul style={styles.historyMetaList}>
                {carteirinha.numero_carteirinha && (
                    <li style={styles.historyMetaItem}>
                        <FiHash size={12} color="#94A3B8" />
                        <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{carteirinha.numero_carteirinha}</span>
                    </li>
                )}
                {carteirinha.validade_carteirinha && (
                    <li style={styles.historyMetaItem}>
                        <FiCalendar size={12} color="#94A3B8" />
                        <span>{formatDate(carteirinha.validade_carteirinha)}</span>
                    </li>
                )}
            </ul>
        </article>
    );
}

// ---------- Estilos ----------

const PRIMARY = '#059669';
const TEXT    = '#0F172A';
const MUTED   = '#64748B';

const styles = {
    container: {
        maxWidth: 1024,
        margin: '0 auto',
        fontFamily: "'Inter', 'Roboto', system-ui, sans-serif",
        color: TEXT,
    },

    // Loading
    loading: {
        padding: '4rem 1rem', textAlign: 'center', color: MUTED,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem',
    },
    spinner: {
        width: 32, height: 32, border: '3px solid #E2E8F0',
        borderTopColor: PRIMARY, borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    // Spinners pequenos pros botões de ação (digital = branco; assinado = cinza).
    btnSpinner: {
        width: 16, height: 16, flexShrink: 0,
        border: '2px solid rgba(255,255,255,0.45)',
        borderTopColor: '#fff', borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
    },
    btnSpinnerDark: {
        width: 16, height: 16, flexShrink: 0,
        border: '2px solid #CBD5E1',
        borderTopColor: '#475569', borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
    },

    // Header
    header: { marginBottom: '1.75rem' },
    title: {
        fontSize: '1.875rem', fontWeight: 700, color: TEXT,
        margin: '0 0 0.4rem', letterSpacing: '-0.02em',
    },
    subtitle: { color: MUTED, fontSize: '0.95rem', margin: 0 },

    // Hero (split)
    hero: {
        display: 'flex',
        gap: '2rem',
        alignItems: 'flex-start',
        marginBottom: '3rem',
    },
    imageColumn: {
        flex: '1 1 320px',
        minWidth: 0,
        maxWidth: 440,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1rem',
    },
    infoColumn: {
        flex: '1 1 320px',
        minWidth: 0,
    },

    // Toggle Digital/Física
    toggleContainer: {
        display: 'inline-flex',
        background: '#F1F5F9',
        padding: 4,
        borderRadius: 12,
        gap: 4,
    },
    toggleBtn: (active) => ({
        padding: '0.5rem 0.95rem',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.85rem',
        backgroundColor: active ? '#fff' : 'transparent',
        color: active ? PRIMARY : '#475569',
        boxShadow: active ? '0 1px 3px 0 rgba(15, 23, 42, 0.08)' : 'none',
        transition: 'all 0.15s ease',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        fontFamily: 'inherit',
    }),

    imageWrapper: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 280,
    },
    imageDigital: {
        maxWidth: '100%',
        width: 360,
        height: 'auto',
        borderRadius: 16,
        boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
    },
    imageFisica: {
        maxWidth: '100%',
        width: 400,
        height: 'auto',
        borderRadius: 16,
        boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
    },
    imageError: {
        color: '#E11D48',
        fontSize: '0.875rem',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '1rem 1.25rem',
        background: '#FFF1F2',
        border: '1px solid #FECDD3',
        borderRadius: 10,
    },

    // Placeholder (sem imagem aprovada)
    imagePlaceholder: {
        width: '100%',
        minHeight: 360,
        borderRadius: 16,
        border: '1.5px dashed',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1.5rem',
        textAlign: 'center',
        gap: '0.5rem',
    },
    placeholderTitle: { fontSize: '1.05rem', fontWeight: 700, margin: '0.5rem 0 0.25rem' },
    placeholderText: { color: MUTED, fontSize: '0.875rem', margin: 0, maxWidth: 280, lineHeight: 1.5 },

    // Info column
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
        padding: '0.35rem 0.75rem',
        borderRadius: 999,
        border: '1px solid',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginBottom: '0.85rem',
    },
    activeTitle: {
        fontSize: '1.75rem',
        fontWeight: 700,
        color: TEXT,
        margin: '0 0 0.4rem',
        letterSpacing: '-0.02em',
    },
    activeSubtitle: {
        fontSize: '0.95rem',
        color: MUTED,
        margin: '0 0 1.5rem',
        lineHeight: 1.5,
    },

    infoList: {
        listStyle: 'none',
        padding: 0,
        margin: '0 0 1.75rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
    infoRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.7rem 0',
        borderBottom: '1px solid #F1F5F9',
        gap: '1rem',
    },
    infoLabel: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        fontSize: '0.8rem',
        color: MUTED,
        fontWeight: 500,
    },
    infoValue: {
        fontSize: '0.9rem',
        color: TEXT,
        fontWeight: 600,
        textAlign: 'right',
    },
    infoSub: {
        fontSize: '0.8rem',
        color: MUTED,
        fontWeight: 400,
    },

    actions: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
    actionPrimary: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        padding: '0.85rem 1.25rem',
        background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
        color: '#fff',
        border: 'none',
        borderRadius: 10,
        fontWeight: 600,
        fontSize: '0.9rem',
        textDecoration: 'none',
        cursor: 'pointer',
        boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.4)',
        fontFamily: 'inherit',
    },
    actionSecondary: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        padding: '0.75rem 1.25rem',
        background: '#fff',
        color: '#475569',
        border: '1.5px solid #E2E8F0',
        borderRadius: 10,
        fontWeight: 600,
        fontSize: '0.9rem',
        textDecoration: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
    },

    // Histórico
    historySection: { marginTop: '0.5rem' },
    historyTitle: {
        fontSize: '1.1rem',
        fontWeight: 700,
        color: TEXT,
        margin: '0 0 1rem',
        letterSpacing: '-0.01em',
    },
    historyGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '0.85rem',
    },
    historyCard: {
        background: '#fff',
        border: '1px solid #E2E8F0',
        borderRadius: 12,
        padding: '1rem 1.1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        boxShadow: '0 1px 3px 0 rgba(15, 23, 42, 0.05)',
    },
    historyTop: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    historyYear: {
        fontSize: '1.25rem',
        fontWeight: 700,
        color: TEXT,
        letterSpacing: '-0.01em',
    },
    statusChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem',
        padding: '0.25rem 0.55rem',
        borderRadius: 999,
        border: '1px solid',
        fontSize: '0.7rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
    },
    historyMetaList: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.35rem',
    },
    historyMetaItem: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        fontSize: '0.8rem',
        color: '#475569',
    },

    // Empty
    emptyCard: {
        textAlign: 'center',
        padding: '3rem 1.5rem',
        background: '#fff',
        borderRadius: 16,
        border: '1px solid #E2E8F0',
        maxWidth: 500,
        margin: '2rem auto',
    },
    emptyTitle: {
        color: TEXT, fontSize: '1.25rem', fontWeight: 700, margin: '0 0 0.5rem',
    },
    emptyMessage: {
        color: MUTED, fontSize: '0.9rem', lineHeight: 1.6, margin: '0 0 1.5rem',
    },
    primaryBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.75rem 1.5rem',
        background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
        color: '#fff',
        border: 'none',
        borderRadius: 10,
        fontWeight: 600,
        fontSize: '0.9rem',
        cursor: 'pointer',
        boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.4)',
        fontFamily: 'inherit',
    },
};
